/**
 * ─── Scenic Solitude — Centralized Theme ─────────────────────────────────────
 *
 * Switch between presets by changing `activeTheme` below.
 *
 *   'dark'  — original dark forest navbar + footer
 *   'light' — warm cream navbar + footer (light theme)
 */

type ThemePreset = {
  /** Tailwind bg class — used directly on Navbar (scrolled) + Footer */
  surfaceDark: string
  /** Raw hex — available for <html> or CSS var usage if needed */
  surfaceDarkBg: string

  surfaceDarker: string
  surfaceLight: string
  surfaceAlt: string

  // ── Text on the dark/light surface (Navbar scrolled + Footer) ──────────────
  textDark: string   // primary body / links
  textDarkMuted: string   // section labels, secondary
  textDarkFaint: string   // copyright, very quiet
  textDarkHover: string   // hover state
  textDarkSubtitle: string   // logo subtitle under nav (Row 1)

  // ── Footer-specific text ────────────────────────────────────────────────────
  footerBody: string   // brand blurb, link lists
  footerLabel: string   // "Explore" / "Contact" column headers
  footerMeta: string   // bottom bar copyright text
  footerBorder: string   // bottom bar divider
  footerLinkHover: string   // hover on footer links
  footerWaLink: string   // WhatsApp CTA text

  // ── Borders on dark/light surfaces ─────────────────────────────────────────
  borderDark: string

  // ── Navbar — transparent state (over hero photo) ────────────────────────────
  navTransparentText: string
  navTransparentBorder: string
  navTransparentReserve: string

  // ── Navbar — scrolled state ─────────────────────────────────────────────────
  navScrolledText: string
  navScrolledBorder: string
  navScrolledReserve: string
  navScrolledHamburger: string   // hamburger icon color
  navLinkDivider: string     // the thin vertical line between links
}

// ── ① Change this to switch the whole site ───────────────────────────────────
const activeTheme: 'dark' | 'light' = 'light'

// ── Dark forest preset (original) ────────────────────────────────────────────
const darkTheme: ThemePreset = {
  surfaceDark: 'bg-forest-800',
  surfaceDarkBg: '#1e3020',

  surfaceDarker: 'bg-forest-950',
  surfaceLight: 'bg-cream-100',
  surfaceAlt: 'bg-cream-200',

  textDark: 'text-cream-100/85',
  textDarkMuted: 'text-cream-300/60',
  textDarkFaint: 'text-cream-300/40',
  textDarkHover: 'hover:text-white',
  textDarkSubtitle: 'text-white/70',

  footerBody: 'text-cream-100/80',
  footerLabel: 'text-cream-300/60',
  footerMeta: 'text-cream-300/60',
  footerBorder: 'border-cream-300/20',
  footerLinkHover: 'hover:text-white',
  footerWaLink: 'text-cream-300/80 hover:text-white',

  borderDark: 'border-cream-300/20',

  navTransparentText: 'text-white',
  navTransparentBorder: 'border-white/30',
  navTransparentReserve: 'border-white/60 text-white hover:bg-white hover:text-ink',

  navScrolledText: 'text-white',
  navScrolledBorder: 'border-cream-300/10',
  navScrolledReserve: 'border-white/50 text-white hover:bg-white hover:text-ink',
  navScrolledHamburger: 'text-cream-300',
  navLinkDivider: 'after:bg-white/20',
}

// ── Light cream preset ────────────────────────────────────────────────────────
const lightTheme: ThemePreset = {
  surfaceDark: 'bg-[#f8f5efff]',
  surfaceDarkBg: '#f5e6c8',

  surfaceDarker: 'bg-cream-100',
  surfaceLight: 'bg-cream-100',
  surfaceAlt: 'bg-cream-200',

  textDark: 'text-ink',
  textDarkMuted: 'text-ink-soft',
  textDarkFaint: 'text-ink-muted',
  textDarkHover: 'hover:text-ink',
  textDarkSubtitle: 'text-ink-soft',

  footerBody: 'text-ink',
  footerLabel: 'text-ink-soft',
  footerMeta: 'text-ink-soft',
  footerBorder: 'border-ink/20',
  footerLinkHover: 'hover:text-ink-soft',
  footerWaLink: 'text-ink hover:text-ink-soft',

  borderDark: 'border-ink/10',

  // transparent state — stays white so it reads over hero photos
  navTransparentText: 'text-white',
  navTransparentBorder: 'border-white/30',
  navTransparentReserve: 'border-white/60 text-white hover:bg-white hover:text-ink',

  // scrolled state — ink text on cream bar
  navScrolledText: 'text-ink',
  navScrolledBorder: 'border-ink/10',
  navScrolledReserve: 'border-ink/40 text-ink hover:bg-ink hover:text-cream-50',
  navScrolledHamburger: 'text-ink',
  navLinkDivider: 'after:bg-ink/15',
}

// ── Export the active preset ──────────────────────────────────────────────────
export const theme: ThemePreset = activeTheme === 'light' ? lightTheme : darkTheme
